import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Modal } from 'react-bootstrap'
import 'react-time-picker/dist/TimePicker.css'
import 'react-clock/dist/Clock.css'
import { useDropzone } from 'react-dropzone'
import { useFormik } from 'formik'
import Swal from 'sweetalert2'
import { propertyBulkUpload } from './Property_Api'
import * as XLSX from 'xlsx';
import * as Yup from 'yup'

interface excelSheetDataType {
  'Property Type': string
  'Property Title': string
  'Carpet Area': number
  'Area Unit': string
  'Address 1': string
  'Address 2': string
  'Province/State': string
  'City': string
  'pin/zip': number
  'Expected Rent': number
  'Property Category': string
  'Property Furnishing Type': string
  'is Agent': boolean
  'Owner Name'?: string
  'Owner Email'?: string
  'Owner Contact'?: string
}

const modalsRoot = document.getElementById('root-modals') || document.body

interface BulkUploadModalDataType {
  show: Boolean
  handleClose: () => void
  getProperty: () => Promise<void>
}

const BulkPropertyUploadModal = ({ show, handleClose, getProperty }: BulkUploadModalDataType) => {
  const errorRef = useRef<HTMLDivElement>(null)
  const modalHeaderRef = useRef<HTMLDivElement>(null)
  const [uploadDocumentLoading, setUploadDocumentLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>("")

  // dropzone code start
  const { acceptedFiles, fileRejections, getRootProps, getInputProps, isDragActive } = useDropzone({
    maxFiles: 1,
    accept: {
      'application/vnd.ms-excel': [],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': []
    },
  })

  const acceptedFileItems = acceptedFiles.map((file) => {
    return (
      // @ts-ignore
      <li key={file.path}>
        {
          // @ts-ignore
          file.path
        }{' '}
        - {file.size} bytes
      </li>
    )
  })

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    // @ts-ignore
    <li key={file.path}>
      {
        // @ts-ignore
        file.path
      }{' '}
      - {file.size} bytes
      <ul>
        {errors.map((e) => (
          <li key={e.code}>{e.message}</li>
        ))}
      </ul>
    </li>
  ))
  // dropzone code ends

  const allowedPropertyType = ['Flat/Apartment', 'Independent Floor', 'Independent House', '1 RK', 'Studio Apartment', 'Serviced Apartment', 'Other']
  const allowedAreaUnit = ['Square Meter', 'Square Feet']
  const allowedProvince = ["Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa",
    "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
    "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
    "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh",
    "Dadra and Nagar Haveli", "Daman and Diu", "Lakshadweep", "Ladakh", "Delhi", "Puducherry",
  ]
  const allowedPropertyCategory = ['Commercial', 'Residential']
  const allowedPropertyFurnishingType = ['Unfurnished', 'Semi Furnished', 'Fully Furnished']

  const validationSchema = Yup.object({
    'Property Type': Yup.string().oneOf(allowedPropertyType).required('Property type is required'),
    'Property Title': Yup.string()
      .matches(/^[A-Za-z0-9\s]*$/, 'Only alphanumeric characters and spaces are allowed')
      .min(3, 'Minimum 3 characters required')
      .max(50, 'Maximum 50 characters are allowed')
      .required('Property title is required'),
    'Carpet Area': Yup.number()
      .typeError('Carpet area must be a number')
      .integer('Carpet area must be an integer')
      .positive('Carpet area must be a positive number')
      .min(1, 'Carpet area must be at least 1')
      .max(99999, 'Carpet area cannot be more than 99999')
      .required('Carpet area is required'),
    "Area Unit": Yup.string().oneOf(allowedAreaUnit).required('Area unit is required'),
    'Address 1': Yup.string()
      .min(3, 'Minimun 3 characters required')
      .max(50, 'Maximum 50 characters are allowed')
      .required('Address is required'),
    "Address 2": Yup.string()
      .min(3, 'Minimun 3 characters required')
      .max(50, 'Maximum 50 characters are allowed')
      .optional(),
    // country_id: Yup.number().required('Country required'),
    "Province/State": Yup.string().oneOf(allowedProvince).required('Province / state is required'),
    "City": Yup.string()
      .min(3, 'Minimum 3 characters required')
      .max(50, 'Maximum 50 characters are allowed')
      .required('City is required'),
    "pin/zip": Yup.number()
      .typeError('Pincode must be a number')
      .positive('Pincode must be a positive number')
      .integer('Pincode must be an integer')
      .min(100000, 'Pincode must be at least 6 digits')
      .max(999999, 'Pincode cannot be more than 6 digits')
      .required('Pincode is required'),
    "Expected Rent": Yup.number()
      .typeError('Expected Rent must be a number')
      .positive('Expected Rent must be a positive number')
      .integer('Expected Rent must be an integer')
      .required('Expected Rent is required'),
    "is Agent": Yup.boolean().required('Please select one'),
    "Owner Name": Yup.string().when('is Agent', (val, schema) => {
      if (val === true) return Yup.string().required('Owner name required for agent-assigned property')
      else return Yup.string().notRequired()
    }),
    "Owner Email": Yup.string().when('is Agent', (val, schema) => {
      if (val === true)
        return Yup.string().required('Owner email required for agent-assigned property').email('Wrong email format')
      else return Yup.string().notRequired()
    }),
    "Owner Contact": Yup.string().when('is Agent', (val, schema) => {
      if (val === true) return Yup.string().required('Owner phone number required for agent-assigned property.')
      else return Yup.string().notRequired()
    }),
    "Property Category": Yup.string().oneOf(allowedPropertyCategory).required('Property category is required'),
    "Property Furnishing Type": Yup.string().required('Furnishing type is required'),
  })

  const bulkPropertyUpload = () => {
    setUploadDocumentLoading(true)
    const reader = new FileReader()
    reader.onloadend = async () => {
      const base64String = reader.result
      try {
        const { data: res } = await propertyBulkUpload(
          // @ts-ignore
          base64String.split(',')[1]
        )
        if (res.code === "200") {
          acceptedFiles.pop()
          setUploadDocumentLoading(false)
          handleClose()
          getProperty()
          Swal.fire({
            position: 'center',
            icon: 'success',
            title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
            text: res.msg_details,
            allowOutsideClick: true,
          })
        }
        else {
          setUploadDocumentLoading(false)
          setError(`${res.msg.charAt(0).toUpperCase() + res.msg.slice(1)}: ${res.msg_details}`)
        }
      }
      catch (error: any) {
        setUploadDocumentLoading(false)
        setError(`Error: ${error}`)
      }
    }
    reader.readAsDataURL(acceptedFiles[0])
  }

  const handleSubmit = () => {
    if (acceptedFiles.length === 1) {
      (async () => {
        const ab = await acceptedFiles[0].arrayBuffer();
        const excelSheet = XLSX.read(ab);
        const ws = excelSheet.Sheets[excelSheet.SheetNames[0]]; // get the first worksheet
        const data: excelSheetDataType[] = XLSX.utils.sheet_to_json(ws); // generate objects
        const requiredColumns: string[] = ["Property Type", "Property Title", "Carpet Area", "Area Unit", "Address 1", "Address 2", "Province/State", "City", "pin/zip", "Expected Rent", "Property Category", "Property Furnishing Type", "is Agent"];
        if (data && data.length > 0) {
          const columnsPresent: string[] = data && data.length > 0 ? Object.keys(data[0]) : [];
          if (requiredColumns.length > columnsPresent.length) {
            setError("Error: Please recheck the excel file to ensure all columns align with the standard template and are mandatory")
            return;
          }

          // Check if each element in the arrays is equal
          for (let k = 0; k < requiredColumns.length; k++) {
            if (requiredColumns[k] !== columnsPresent[k]) {
              setError("Error: Please recheck the excel file to ensure all columns align with the standard template and are mandatory")
              return;
            }
          }

          const arrayOfObjectsSchema = Yup.array().of(validationSchema);
          arrayOfObjectsSchema.validate(data)
            .then(valid =>
              bulkPropertyUpload()
            )
            .catch(err => setError(err.toString())
            );
        }
        else {
          setError("Error: Excel sheet is empty")
        }
      })();
    } else {
      setError(`Error: No document found. Add excel sheet to upload`)
    }
  }

  // const handleSubmit = () => {
  //   if (acceptedFiles.length === 1) {
  //     setUploadDocumentLoading(true)
  //     try {
  //       const reader = new FileReader()
  //       reader.onloadend = async () => {
  //         const base64String = reader.result
  //         const { data: res } = await propertyBulkUpload(
  //           // @ts-ignore
  //           base64String.split(',')[1]
  //         )
  //         if (res.code === "200") {
  //           acceptedFiles.pop()
  //           setUploadDocumentLoading(false)
  //           handleClose()
  //           getProperty()
  //           Swal.fire({
  //             position: 'center',
  //             icon: 'success',
  //             title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
  //             text: res.msg_details,
  //             allowOutsideClick: true,
  //           })
  //         } else {
  //           setUploadDocumentLoading(false)
  //           setError(`${res.msg.charAt(0).toUpperCase() + res.msg.slice(1)}: ${res.msg_details}`)
  //         }
  //       }
  //       reader.readAsDataURL(acceptedFiles[0])
  //     }
  //     catch (error: any) {
  //       setUploadDocumentLoading(false)
  //       setError(`Error: ${error}`)
  //     }
  //   } else {
  //     setError(`No document found: Add excel sheet to upload`)
  //   }
  // }

  // wipe out the error every 2 sec
  useEffect(() => {
    if (error) {
      setTimeout(() => {
        setError('')
      }, 3000)
    }
  }, [error])

  const initialValues = {
    file: '',
  }

  const formik = useFormik({
    initialValues,
    onSubmit: () => handleSubmit(),
  })

  return createPortal(
    <Modal
      id='kt_modal_create_app'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered mw-900px'
      show={show ? true : false}
      onHide={handleClose}
      backdrop={true}
    >
      {/* Begin: Modal header */}
      <div className='modal-header pt-5 pb-5' ref={modalHeaderRef}>
        {/* Begin: Modal Heading */}
        <h2>Bulk Property Upload</h2>
        {/* End: Modal Heading */}
        {/* Begin::Close button */}
        <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={handleClose}>
          <i className='bi bi-x-lg' onClick={handleClose}></i>
        </div>
        {/* end::Close button */}
      </div>
      {/* End: Modal header */}
      {/* Begin: Error msg section */}
      {error && (
        <div ref={errorRef} className='d-flex justify-content-center align-items-center px-2' style={{ backgroundColor: 'red' }}>
          <span className='fv-help-block' style={{ color: 'white' }}>
            {error}
          </span>
        </div>
      )}
      {/* End: Error msg section */}
      {/* Begin: Modal body */}
      <div className='modal-body'>
        <div id='kt_account_profile_details' className='collapse show'>
          <form onSubmit={formik.handleSubmit} className='form'>
            <div className='card-body'>
              {/*begin::Dropzone field */}
              <label className='d-flex align-items-center fs-5 fw-semibold mb-2 ps-6'>
                <span className='required'>Upload Property Excel Sheet</span>
                <i
                  className='fas fa-exclamation-circle ms-2 fs-7'
                  data-bs-toggle='tooltip'
                  title='Upload property excel sheet'
                ></i>
              </label>
              <div className='border border-2 rounded border-dashed row p-2 bg-white mx-4'>
                <section className='border border-2 rounded border-primary border-dashed h-100 symbol bg-light-primary'>
                  <div
                    {...getRootProps({
                      className:
                        'py-4 d-flex flex-column justify-content-center align-items-center',
                    })}
                  >
                    <input {...getInputProps()} />
                    {isDragActive ? (
                      <p>Drop the files here...</p>
                    ) : (
                      <p>Drag and drop files here, or click to select files</p>
                    )}
                    <i className='bi bi-file-earmark-arrow-up-fill fs-4x btn btn-active-color-primary py-0 mb-3'></i>
                    <span className='text-primary fs-7'>
                      Type: *.xls or *.xlsx only
                    </span>
                    <span className='text-primary fs-7'>Max document:1</span>
                  </div>
                </section>
              </div>

              <aside className='px-5'>
                {acceptedFileItems.length > 0 && (
                  <div className='pt-2'>
                    <h4>Document is ready to upload</h4>
                    <ul>{acceptedFileItems}</ul>
                  </div>
                )}
                {fileRejectionItems.length > 0 && (
                  <div className='pt-2'>
                    <span className='fv-help-block color-red'>
                      Upload only one file or select files with allowed formats only
                    </span>
                    {/* <ul>{fileRejectionItems}</ul> */}
                  </div>
                )}
              </aside>
              {/*end::Dropzone field */}
              <div className='d-flex flex-column align-items-center'>
                <button
                  type='submit'
                  className='btn btn-sm btn-primary shadow mt-3'
                  style={{ cursor: 'pointer' }}
                  disabled={uploadDocumentLoading}
                >
                  {uploadDocumentLoading ? (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      Processing...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  ) : (
                    <span>Upload Excel Sheet</span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>,
    modalsRoot
  )
}
export default BulkPropertyUploadModal